import { useEffect, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    TicketDTO,
    TicketMessageDTO,
    useGetTicketByIdApi,
    useSendAdminMessageApi,
    useUpdateTicketStatusApi,
    useCloseTicketApi,
} from '../../../api/ticket-api';
import { apiConfig } from '../../../api/config';
import SvgClose from '../../../assets/icons/Close';
import SvgSave from '../../../assets/icons/Save';
import SvgAttachFile from '../../../assets/icons/AttachFile';
import { BasicModal } from '../../../components/basic/basic-modal';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';

// Helper to get full attachment URL
const getAttachmentUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${apiConfig.host}${path}`;
};

// Helper to check if attachment is an image
const isImageAttachment = (path: string) => {
    const ext = path.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
};

interface ManageTicketModalProps {
    show: boolean;
    selectedTicket?: TicketDTO;
    onHide: () => void;
    onUpdate: () => void;
}

export function ManageTicketModal({ show, selectedTicket, onHide, onUpdate }: ManageTicketModalProps) {
    const [messageText, setMessageText] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const ticketDetailApi = useGetTicketByIdApi();
    const sendMessageApi = useSendAdminMessageApi();
    const updateStatusApi = useUpdateTicketStatusApi();
    const closeTicketApi = useCloseTicketApi();

    const { result, busy, initialized } = ticketDetailApi;

    useEffect(() => {
        if (show && selectedTicket) {
            ticketDetailApi.get({ ticketId: selectedTicket.id! });
            setNewStatus(selectedTicket.status);
        }
    }, [show, selectedTicket]);

    const handleSendMessage = () => {
        if (!messageText.trim() || !selectedTicket) return;

        sendMessageApi.post(
            null,
            {
                ticketId: selectedTicket.id,
                message: messageText,
            },
            () => {
                setMessageText('');
                ticketDetailApi.get({ ticketId: selectedTicket.id! });
                notify.success(translate('message-sent-successfully'));
            },
            notify.error
        );
    };

    const handleUpdateStatus = (status: string) => {
        if (!selectedTicket || status === selectedTicket.status) return;

        updateStatusApi.post(
            null,
            {
                ticketId: selectedTicket.id,
                status: status,
            },
            () => {
                onUpdate();
                setNewStatus(status);
                notify.success(translate('status-updated-successfully'));
            },
            notify.error
        );
    };

    const handleCloseTicket = () => {
        if (!selectedTicket) return;

        bd.msgbox(
            translate('close-ticket'),
            translate('confirm-close-ticket'),
            [
                {
                    title: translate('close'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        closeTicketApi.post(
                            { ticketId: selectedTicket.id! },
                            null,
                            () => {
                                onUpdate();
                                onHide();
                                notify.success(translate('ticket-closed-successfully'));
                                hide();
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const ticket = result?.ticket;
    const messages = result?.messages || [];

    return (
        <BasicModal
            size="xl"
            fullScreen={true}
            show={show}
            onHide={onHide}
            title={
                <div>
                    <T>ticket-details</T>
                    {ticket && <span className="text-muted ms-2">#{ticket.id}</span>}
                </div>
            }
            headerButtons={
                <bd.Flex gap={2}>
                    <bd.Button variant="outline" size="sm" icon={<SvgClose />} onClick={onHide}>
                        <T>close</T>
                    </bd.Button>
                    {ticket && ticket.status !== 'Closed' && (
                        <bd.Button
                            variant="contained"
                            color="secondary"
                            size="sm"
                            onClick={handleCloseTicket}
                            loading={closeTicketApi.busy}
                        >
                            <T>close-ticket</T>
                        </bd.Button>
                    )}
                </bd.Flex>
            }
        >
            {!initialized || busy ? (
                <Loading />
            ) : ticket ? (
                <div className="p-3">
                    {/* Ticket Info */}
                    <bd.Paper className="mb-3 p-3">
                        <bd.Flex gap={3} wrap>
                            <div style={{ flex: '1 1 300px' }}>
                                <div className="mb-2">
                                    <strong><T>user</T>:</strong> {ticket.userName}
                                </div>
                                <div className="mb-2">
                                    <strong><T>subject</T>:</strong> {ticket.subject}
                                </div>
                                <div className="mb-2">
                                    <strong><T>category</T>:</strong>{' '}
                                    <bd.Badge variant="info" size="sm">
                                        {ticket.category}
                                    </bd.Badge>
                                </div>
                            </div>
                            <div style={{ flex: '1 1 300px' }}>
                                <div className="mb-2">
                                    <strong><T>priority</T>:</strong>{' '}
                                    <bd.Badge
                                        variant={
                                            ticket.priority === 'Urgent'
                                                ? 'danger'
                                                : ticket.priority === 'High'
                                                ? 'warning'
                                                : 'info'
                                        }
                                        size="sm"
                                    >
                                        {ticket.priority}
                                    </bd.Badge>
                                </div>
                                <div className="mb-2">
                                    <strong><T>created-at</T>:</strong>{' '}
                                    {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : '-'}
                                </div>
                                <div className="mb-2">
                                    <strong><T>status</T>:</strong>{' '}
                                    <bd.Flex gap={1} align="center" className="d-inline-flex">
                                        {['Open', 'InProgress', 'Resolved', 'Closed'].map(status => (
                                            <bd.Button
                                                key={status}
                                                size="sm"
                                                variant={newStatus === status ? 'contained' : 'outline'}
                                                color={
                                                    status === 'Closed' ? 'secondary' : 'primary'
                                                }
                                                onClick={() => handleUpdateStatus(status)}
                                                loading={updateStatusApi.busy}
                                            >
                                                {status}
                                            </bd.Button>
                                        ))}
                                    </bd.Flex>
                                </div>
                            </div>
                        </bd.Flex>
                    </bd.Paper>

                    {/* Messages */}
                    <bd.Paper className="mb-3 p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <h6 className="mb-3"><T>messages</T></h6>
                        {messages.length === 0 ? (
                            <div className="text-center text-muted py-3">
                                <T>no-messages-yet</T>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {messages.map((msg: TicketMessageDTO) => (
                                    <div
                                        key={msg.id}
                                        className={`p-3 rounded mb-3 ${
                                            msg.senderType === 'Admin' ? 'bg-primary-subtle' : 'bg-light'
                                        }`}
                                    >
                                        <bd.Flex content="between" align="start" className="mb-2">
                                            <strong className={msg.senderType === 'Admin' ? 'text-primary' : ''}>
                                                {msg.senderName || msg.senderType}
                                            </strong>
                                            <small className="text-muted">
                                                {msg.createdAt ? new Date(msg.createdAt).toLocaleString() : ''}
                                            </small>
                                        </bd.Flex>
                                        <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                            {msg.message}
                                        </p>
                                        {/* Attachment display */}
                                        {msg.attachmentPath && (
                                            <div className="mt-2 pt-2 border-top">
                                                {isImageAttachment(msg.attachmentPath) ? (
                                                    <div>
                                                        <a
                                                            href={getAttachmentUrl(msg.attachmentPath)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <img
                                                                src={getAttachmentUrl(msg.attachmentPath)}
                                                                alt="attachment"
                                                                style={{
                                                                    maxWidth: '300px',
                                                                    maxHeight: '200px',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </a>
                                                        <div className="mt-1">
                                                            <a
                                                                href={getAttachmentUrl(msg.attachmentPath)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary small d-inline-flex align-items-center"
                                                            >
                                                                <SvgAttachFile className="me-1" style={{ width: 16, height: 16 }} />
                                                                <T>view-full-image</T>
                                                            </a>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <a
                                                        href={getAttachmentUrl(msg.attachmentPath)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="d-inline-flex align-items-center gap-1 text-primary"
                                                    >
                                                        <SvgAttachFile style={{ width: 16, height: 16 }} />
                                                        <T>download-attachment</T>
                                                        <small className="text-muted ms-1">
                                                            ({msg.attachmentPath.split('/').pop()})
                                                        </small>
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </bd.Paper>

                    {/* Send Message */}
                    {ticket.status !== 'Closed' && (
                        <bd.Paper className="p-3">
                            <h6 className="mb-3"><T>send-reply</T></h6>
                            <bd.TextField
                                as="textarea"
                                rows={4}
                                value={messageText}
                                onChange={(e: any) => setMessageText(e.target.value)}
                                placeholder={translate('write-your-message')}
                                className="mb-3"
                            />
                            <bd.Button
                                variant="contained"
                                size="sm"
                                icon={<SvgSave />}
                                onClick={handleSendMessage}
                                loading={sendMessageApi.busy}
                                disabled={!messageText.trim()}
                            >
                                <T>send-message</T>
                            </bd.Button>
                        </bd.Paper>
                    )}
                </div>
            ) : (
                <div className="text-center p-5">
                    <T>ticket-not-found</T>
                </div>
            )}
        </BasicModal>
    );
}
